import React from "react";

interface FormFieldProps {
  label: string;
  required?: boolean;
  /** Always-visible guidance under the control, e.g. the minimum length. */
  hint?: string;
  /** Replaces the hint once the user has typed something that fails. */
  error?: string | null;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Label, control and a line underneath for the request modals. The rule a
 * field must meet is written under it instead of behind a tooltip, and turns
 * into the error when the value fails it, so nobody has to guess why Next is
 * disabled.
 */
export function FormField({
  label,
  required = false,
  hint,
  error,
  htmlFor,
  className = "",
  children,
}: FormFieldProps) {
  const note = error || hint;
  return (
    <div className={`min-w-0 ${className}`}>
      <label
        htmlFor={htmlFor}
        className="mb-1 block text-sm font-medium text-semantic-text-primary"
      >
        {label}
        {required && (
          <span className="ml-0.5 text-semantic-text-danger" aria-hidden>
            *
          </span>
        )}
      </label>
      {children}
      {note && (
        <p
          className={`mt-1 text-xs ${
            error ? "text-semantic-text-danger" : "text-semantic-text-secondary"
          }`}
        >
          {note}
        </p>
      )}
    </div>
  );
}

interface TextAreaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
}

/** The multi-line counterpart of `GenericInput`, styled with the same tokens. */
export function TextArea({
  id,
  value,
  onChange,
  placeholder,
  invalid = false,
}: TextAreaProps) {
  return (
    <textarea
      id={id}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      rows={5}
      className={`
        block w-full resize-y rounded-sm border bg-semantic-bg-primary px-3 py-2 text-sm
        text-semantic-text-primary placeholder:text-semantic-text-secondary
        focus:outline-none focus:border-semantic-border-brand-primary
        ${invalid ? "border-semantic-border-danger-primary" : "border-semantic-border-secondary"}
      `}
    />
  );
}

/** Two fields side by side from `sm` up, stacked below it. */
export function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;
}
